// Libs
import moment from 'moment';
import classNames from 'classnames/bind';
import { useCallback, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { Timeline, TimelineConnector, TimelineOppositeContent, TimelineItem, TimelineSeparator } from '@mui/lab';
// Components, Layouts, Pages
import { BaseDatePicker, BaseLabel, BaseSelect } from '~/components/common';
import { CommonIconEventTimeline } from '~/assets/svgComponents';
// Others
import { AMBER_500, BLUE_500, GRAY_500, GREEN_600, INDIGO_500, PINK_700 } from '~/utils/constants/color';
import { IFormOrder } from '~/utils/interface/order';
import { formatDate } from '~/utils/helper/common';
import { IBaseOption, IResultCallApiFn } from '~/utils/interface/common';
import { EMPTY_STRING, optionsTimeLimitQuote } from '~/utils/constants/common';
// Styles, images, icons
import styles from './EventTimelineOrderForm.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const EventTimelineOrderForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IFormOrder>();

  const { startDate, startTime, endDate, endTime } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!startDate) return;

    const threeDaysBefore = moment(startDate).clone().subtract(3, 'days');
    setValue('earliestDeliveryDate', formatDate(threeDaysBefore.toString()), { shouldValidate: true });

    if (endDate && moment(startDate).isAfter(endDate)) {
      setValue('endDate', startDate, { shouldValidate: true });
    }
  }, [startDate]);

  useEffect(() => {
    if (!endDate) return;

    const threeDaysAfter = moment(endDate).clone().add(3, 'days');
    setValue('latestPickupDate', formatDate(threeDaysAfter.toString()), { shouldValidate: true });
  }, [endDate]);

  useEffect(() => {
    if (!startTime) return;

    setValue('earliestDeliveryTime', startTime, { shouldValidate: true });
  }, [startTime]);

  useEffect(() => {
    if (!endTime) return;

    setValue('latestPickupTime', endTime, { shouldValidate: true });
  }, [endTime]);

  const fetchOptionsTimeLimit = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsTimeLimitQuote,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Box id='eventTimelineOrderFormComponent' className={cx('container')}>
      <BaseLabel
        required
        styleType='title'
        label={t('form_orders_title_event_timeline')}
        icon={icons.commonIconEventTimeLine}
      />

      <Timeline className={cx('timeline')}>
        <TimelineItem>
          <TimelineSeparator>
            <TimelineConnector className={cx('viewDotWhite')} />
            <CommonIconEventTimeline fill={GRAY_500} />
            <TimelineConnector className={cx('viewDot')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_orders_title_earliest_delivery')}</Typography>
              <Stack direction={'row'} gap={2}>
                <Controller
                  control={control}
                  name='earliestDeliveryDate'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      height={28}
                      width={140}
                      maxDate={new Date(startDate || EMPTY_STRING)}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.earliestDeliveryDate?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='earliestDeliveryTime'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      mode='time'
                      height={28}
                      width={140}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.earliestDeliveryTime?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineSeparator>
            <TimelineConnector className={cx('viewDot')} />
            <CommonIconEventTimeline fill={AMBER_500} />
            <TimelineConnector className={cx('viewDot')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_orders_title_delivery_scheduled')}</Typography>
              <Stack direction={'row'} gap={2}>
                <Controller
                  control={control}
                  name='deliveryScheduledDate'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      height={28}
                      width={140}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.deliveryScheduledDate?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='deliveryScheduledTime'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      mode='time'
                      height={28}
                      width={140}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.deliveryScheduledTime?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='deliveryType'
                  render={({ field: { onChange, value } }) => (
                    <BaseSelect
                      gap={15}
                      height={33}
                      styleType='contained'
                      iconLabel={icons.commonIconReferralSource}
                      callApiFn={fetchOptionsTimeLimit}
                      value={value}
                      onChange={(option) => {
                        onChange(option?.value);
                      }}
                      errorMessage={errors.deliveryType?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineSeparator>
            <TimelineConnector className={cx('viewDot')} />
            <CommonIconEventTimeline fill={BLUE_500} />
            <TimelineConnector className={cx('viewDot')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_orders_title_event_start')}</Typography>
              <Stack direction={'row'} gap={2}>
                <Controller
                  control={control}
                  name='startDate'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      height={28}
                      width={140}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.startDate?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='startTime'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      mode='time'
                      height={28}
                      width={140}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.startTime?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineSeparator>
            <TimelineConnector className={cx('viewDot')} />
            <CommonIconEventTimeline fill={GREEN_600} />
            <TimelineConnector className={cx('viewDot')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_orders_title_event_end')}</Typography>
              <Stack direction={'row'} gap={2}>
                <Controller
                  control={control}
                  name='endDate'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      height={28}
                      width={140}
                      minDate={new Date(startDate || EMPTY_STRING)}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.endDate?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='endTime'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      mode='time'
                      height={28}
                      width={140}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.endTime?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineSeparator>
            <TimelineConnector className={cx('viewDot')} />
            <CommonIconEventTimeline fill={PINK_700} />
            <TimelineConnector className={cx('viewDot')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_orders_title_pickup_scheduled')}</Typography>
              <Stack direction={'row'} gap={2}>
                <Controller
                  control={control}
                  name='pickupScheduledDate'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      height={28}
                      width={140}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.pickupScheduledDate?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='pickupScheduledTime'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      mode='time'
                      height={28}
                      width={140}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.pickupScheduledTime?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineSeparator>
            <TimelineConnector className={cx('viewDot')} />
            <CommonIconEventTimeline fill={INDIGO_500} />
            <TimelineConnector className={cx('viewDotWhite')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_orders_title_latest_pickup')}</Typography>
              <Stack direction={'row'} gap={2}>
                <Controller
                  control={control}
                  name='latestPickupDate'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      height={28}
                      width={140}
                      minDate={new Date(endDate || EMPTY_STRING)}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.latestPickupDate?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='latestPickupTime'
                  render={({ field: { onChange, value } }) => (
                    <BaseDatePicker
                      mode='time'
                      height={28}
                      width={140}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.latestPickupTime?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>
      </Timeline>
    </Box>
  );
};

export default EventTimelineOrderForm;
